import cloneDeep from 'lodash.clonedeep';
import {
  DEFAULT_ELEMENT_WIDGET_DATA,
  DEFAULT_ICON_DATA,
  DEFAULT_LINE_DATA,
  DEFAULT_LINE_ICON_DATA,
  DEFAULT_TEXT_DATA,
} from 'modules/Editor/components/WidgetMenu/SideMenu/ElementsMenu/Elements/Elements.config';
import {
  ElementWidget,
  ElementsConfigData,
} from 'modules/Editor/components/WidgetMenu/SideMenu/ElementsMenu/Elements/Elements.types';
import { RootState, store } from 'modules/Editor/store';
import { selectInfographHeightPx, selectInfographWidthPx } from 'modules/Editor/store/infographSelector';
import { WidgetType } from 'types/widget.types';
import { generateDefaultData as generateShapeData } from 'widgets/BasicShapeWidget/BasicShapeWidget.helpers';
import { BasicShapeType, BasicShapeWidgetData } from 'widgets/BasicShapeWidget/BasicShapeWidget.types';
import { generateDefaultData as generateIconData } from 'widgets/IconWidget/IconWidget.helpers';
import { generateDefaultData as generateImageData } from 'widgets/ImageWidget/ImageWidget.helpers';
import { ImageWidgetData } from 'widgets/ImageWidget/ImageWidget.types';
import { generateDefaultData as generateLineIconData } from 'widgets/Lines/LineIconWidget/LineIconWidget.helpers';
import { LineIconWidgetData } from 'widgets/Lines/LineIconWidget/LineIconWidget.types';
import { generateDefaultData as generateLineData } from 'widgets/Lines/LineWidget/LineWidget.helpers';
import { LineWidgetData } from 'widgets/Lines/LineWidget/LineWidget.types';
import { generateDefaultData as generateTextData } from 'widgets/TextBasedWidgets/TextWidget/TextWidget.helpers';
import { TextWidgetData } from 'widgets/TextBasedWidgets/TextWidget/TextWidget.types';
import { NewWidget } from 'widgets/Widget.types';

/**
 * Generate element data from elements config data
 * @returns
 * @category Elements
 * @param elementData
 */
export const createElementWidgetDataFromConfig = (elementData: ElementsConfigData) => {
  const groupWidgets = [] as NewWidget[];

  const { elementWidgetsMap } = elementData;

  elementWidgetsMap.forEach((elementWidget: ElementWidget) => {
    const { widgetType, widgetData } = elementWidget;

    if (!widgetType) return;

    // Generate widget data based on widget type
    switch (widgetType) {
      case WidgetType.BasicShape:
        const basicShapeDataOverride = widgetData as Partial<BasicShapeWidgetData>;
        const { type, border } = basicShapeDataOverride;
        groupWidgets.push({
          ...generateShapeData(type ?? BasicShapeType.Rectangle, !!border, basicShapeDataOverride),
        });
        break;
      case WidgetType.Image:
        const storeState = store.getState() as RootState;
        const infographWidthPx = selectInfographWidthPx(storeState);
        const infographHeightPx = selectInfographHeightPx(storeState);
        const infographSize = { width: infographWidthPx, height: infographHeightPx };
        const imgWidgetSize = { width: 100, height: 100 };

        const imageDataOverride = widgetData as Partial<ImageWidgetData>;
        const imageWidget = generateImageData(
          imageDataOverride.url ?? 'src',
          imgWidgetSize,
          infographSize,
        ) as NewWidget;
        imageWidget.widgetData = { ...imageWidget.widgetData, ...imageDataOverride };
        groupWidgets.push({
          ...imageWidget,
        });
        break;
      case WidgetType.Icon:
        const { iconWidgetData } = elementWidget;
        if (!iconWidgetData) return;
        const iconData = { ...DEFAULT_ICON_DATA, ...iconWidgetData };
        const { iconId, viewBox, topPxOverride, leftPxOverride, mono, iconConfig } = iconData;
        const iconWidget = generateIconData(iconId, viewBox, topPxOverride, leftPxOverride, mono, iconConfig);
        groupWidgets.push({
          ...iconWidget,
        });
        break;
      case WidgetType.Text:
        const { textType: defaultTextType, text: defaultText, hasLink: defaultHasLinkFlag } = DEFAULT_TEXT_DATA;
        const textDataOverride = widgetData as Partial<TextWidgetData>;
        groupWidgets.push({
          ...generateTextData(defaultTextType, defaultText, defaultHasLinkFlag, textDataOverride),
        });
        break;
      case WidgetType.Line:
        const { lineType: defaultLineWidgetType } = DEFAULT_LINE_DATA;
        const lineDataOverride = widgetData as Partial<LineWidgetData>;
        groupWidgets.push({
          ...generateLineData(lineDataOverride?.type ?? defaultLineWidgetType, lineDataOverride),
        });
        break;
      case WidgetType.LineIcon:
        const { lineType: defaultLineIconType } = DEFAULT_LINE_ICON_DATA;
        const lineIconDataOverride = widgetData as Partial<LineIconWidgetData>;
        const lineIconType = lineIconDataOverride.type ?? defaultLineIconType;
        groupWidgets.push({
          ...generateLineIconData(lineIconType, lineIconDataOverride),
        });
        break;
      default:
        break;
    }
  });

  return {
    widgetType: WidgetType.Group,
    widgetData: {
      ...DEFAULT_ELEMENT_WIDGET_DATA,
    },
    groupWidgets,
  };
};

/**
 * Find the base widget from members that is closest to top left or 0, 0
 * @param groupWidgets
 */
export const findBaseWidgetFromGroup = (groupWidgets: NewWidget[]) => {
  let baseWidgetIdx: number | null = null;
  let baseWidget: NewWidget | null = null;

  for (let i = 0; i < groupWidgets.length - 1; i++) {
    const { widgetData } = groupWidgets[i];
    if (
      !baseWidget ||
      widgetData.leftPx < baseWidget!.widgetData.leftPx ||
      (widgetData.leftPx === baseWidget!.widgetData.leftPx && widgetData.topPx < baseWidget!.widgetData.topPx)
    ) {
      baseWidgetIdx = i;
      baseWidget = groupWidgets[i];
    }
  }

  return { baseWidget: groupWidgets[baseWidgetIdx ?? 0], baseWidgetIdx: baseWidgetIdx ?? 0 };
};
/**
 * Updates the position of the group members relative to left most widget after setting base position for left most widget
 * @param initialWidgetData
 * @param newBasePosition
 */
export const updateGroupMembersPosition = (
  initialWidgetData: NewWidget,
  newBasePosition: { topPx: number; leftPx: number },
) => {
  const newWidgetData = cloneDeep(initialWidgetData) as NewWidget;
  // initial base widget data, we want to keep these separate for reference
  const { groupWidgets: initialGroupWidgets } = initialWidgetData;
  const { groupWidgets } = newWidgetData;
  const { baseWidgetIdx } = findBaseWidgetFromGroup(initialGroupWidgets!);
  const { topPx: baseTopPx, leftPx: baseLeftPx } = newBasePosition;

  if (groupWidgets && groupWidgets.length > 0) {
    const baseWidget = initialGroupWidgets![baseWidgetIdx] as NewWidget;

    // get the default top and left px for the base widget
    const defaultTopPx = baseWidget.widgetData.topPx;
    const defaultLeftPx = baseWidget.widgetData.leftPx;

    // calculate the offset for each widget in the group
    const offsetTopPx = baseTopPx + defaultTopPx;
    const offsetLeftPx = baseLeftPx + defaultLeftPx;

    groupWidgets.forEach((widget, idx) => {
      const { widgetData } = widget;
      const { topPx, leftPx } = widgetData;
      // update the widget data with the new top and left px based on the offset values above
      return (widget.widgetData = {
        ...widgetData,
        ...{ topPx: topPx + offsetTopPx, leftPx: leftPx + offsetLeftPx },
      });
    });
  }

  return { ...newWidgetData };
};
